<template>
  <div id="calendar" class="container-fluid mt-4">
    <TopNav
      :title="$t('GENERAL.LABEL.EVENTS')"
      :showOpenMainNav="true"
      :icon="['far', 'calendar']"
    >
      <NavItem
        icon="fal fa-plus"
        :text="$t('EVENTS.LINK.ADD_EVENT')"
        @click.native="addEvent(null)"
      />
    </TopNav>
    <div class="mt-4">
      <CalendarCustomHeader
        @previous="previous"
        @next="next"
        @rerender="rerender"
        @changeView="changeView($event)"
        :currentMonth="currentMonth"
      />
      <div
        class="calendar-view"
        :class="{ hide: currentViewType === 'listMonth' }"
      >
        <div
          class="loading-calendar d-flex align-items-center justify-content-center"
          v-if="isLoading"
        >
          <HMSpinner />
        </div>
        <FullCalendar ref="calendar" :options="calendarOptions" />
      </div>
      <EventsList
        class="list-view"
        :class="{ 'show-list': currentViewType === 'listMonth' }"
        :events="selectedMonthEvents"
        :selectedDate="selectedDate"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import TopNav from '@/web/components/navigation/TopNav';
import NavItem from '@/web/components/navigation/NavItem.vue';
import { sanitizeHtmlFully } from '@/core/utils/utils';

import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { mapGetters } from 'vuex';
import EventStartStopModal from '@/web/views/admin/events/EventStartStopModal';
import CalendarCustomHeader from '@/web/views/admin/events/CalendarCustomHeader';
import EventsList from '@/web/views/admin/events/EventsList';
import { errorToMessage } from '@/core/utils/errors';

export default {
  name: 'Calendar',
  components: {
    FullCalendar,
    CalendarCustomHeader,
    TopNav,
    NavItem,
    EventsList,
  },
  computed: {
    ...mapGetters({
      events: 'event/events',
      selectedEventType: 'event/selectedEventType',
      currentViewType: 'event/currentViewType',
      eventsLoading: 'event/eventsLoading',
      teacherCalendarSelectedDate: 'event/teacherCalendarSelectedDate',
    }),
    selectedMonthEvents() {
      if (!this.events) return [];
      return this.events.filter(event => {
        const eventDate = moment(event.event_start_datetime);
        return eventDate.isSame(this.selectedDate, 'month');
      });
    },
  },
  data() {
    return {
      isLoading: true,
      isSmallScreen: false,
      selectedDate: moment(new Date()),
      calendarRef: null,
      currentMonth: '',
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
        allDaySlot: false,
        headerToolbar: false,
        height: 700,
        firstDay: 0,
        dayMaxEvents: false,
        eventDisplay: 'block',
        eventBackgroundColor: 'transparent',
        eventTextColor: '#101618',
        eventBorderColor: 'transparent',
        initialView: 'dayGridMonth',
        dateClick: this.handleDateClick,
        events: this.loadEvents,
        editable: true,
        selectable: true,
        selectMirror: true,
        weekends: true,
        select: this.handleDateSelect,
        eventClick: this.handleEventClick,
        eventsSet: this.handleEvents,
        eventClassNames: this.setClassNames,
        eventDrop: this.eventDrop,
        eventDidMount: this.eventDidMount,
      },
    };
  },
  methods: {
    sanitizeHtmlFully,
    async loadEvents(fetchInfo, successCallback, failureCallback) {
      this.isLoading = true;
      const { start, end, startStr, endStr, timeZone, callback } = fetchInfo;
      const params = {
        fromDate: moment(start).subtract({ days: 1 }),
        toDate: moment(end).add({ days: 1 }),
      };

      try {
        await this.$store.dispatch('event/getEvents', params);
        const mappedEvents = this.events.map(event => {
          const startDateTime = moment(event.event_start_datetime);
          const endDateTime = moment(event.event_end_datetime);
          const hasSubstitues = event.substitute_instructors?.length > 0;

          return {
            id: event.id,
            title:
              (hasSubstitues ? '[S]' : '') +
              event.event_name +
              (event.number_of_registrations > 0
                ? ' [' + event.number_of_registrations + ']'
                : ''),
            event: event,
            start: startDateTime.toISOString(),
            end: endDateTime.toISOString(),
            allDay: false,
            is_www_event: event.is_www_event,
          };
        });
        successCallback(mappedEvents);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load events',
          text: errorToMessage(error.response, `Request failed: ${error}`),
        });
        failureCallback(error);
      } finally {
        this.isLoading = false;
      }
    },
    setClassNames(arg) {
      const classNames = [...arg.event.classNames];
      const event = arg.event?.extendedProps?.event;

      if (event) {
        classNames.push(event.event_type);
        if (event.is_cancelled) {
          classNames.push('cancelled-event');
        }
        if (
          this.selectedEventType &&
          event.event_type !== this.selectedEventType.code
        ) {
          classNames.push('d-none');
        } else {
          classNames.push('visible');
        }
        if (event.is_www_event) {
          classNames.push('www-event');
        }
      }
      return classNames;
    },
    setDisplayMonthAndYear() {
      if (!this.calendarRef) return;
      const calendarApi = this.calendarRef.getApi();
      this.selectedDate = moment(calendarApi.getDate());
      this.currentMonth = this.selectedDate.format('MMM YYYY');
    },
    setInitialDate() {
      if (!this.teacherCalendarSelectedDate) return;
      if (!this.calendarRef) return;
      const calendarApi = this.calendarRef.getApi();
      calendarApi.gotoDate(this.teacherCalendarSelectedDate);
    },
    next() {
      if (!this.calendarRef) return '';
      const calendarApi = this.calendarRef.getApi();
      calendarApi.next();
      this.setDisplayMonthAndYear();
      this.$store.dispatch(
        'event/setTeacherCalendarSelectedDate',
        calendarApi.getDate()
      );
    },
    previous() {
      if (!this.calendarRef) return '';
      const calendarApi = this.calendarRef.getApi();
      calendarApi.prev();
      this.setDisplayMonthAndYear();
      this.$store.dispatch(
        'event/setTeacherCalendarSelectedDate',
        calendarApi.getDate()
      );
    },
    rerender() {
      if (!this.calendarRef) return '';
      const calendarApi = this.calendarRef.getApi();
      calendarApi.render();
    },
    refetchEvents() {
      if (!this.calendarRef) return '';
      const calendarApi = this.calendarRef.getApi();
      calendarApi.refetchEvents();
    },
    changeView(viewName) {
      const calendarApi = this.calendarRef.getApi();
      calendarApi.changeView(viewName);
      this.$store.dispatch('event/setCurrentViewType', viewName);
    },
    setListView() {
      // TODO
    },
    handleDateClick(arg) {
      this.addEvent(arg.dateStr);
    },
    eventDidMount(info) {
      const event = info.event.extendedProps.event;
      let toolTipContent = event.event_name;
      if (event.number_of_registrations) {
        const regInfo = event.registration_limit
          ? `${event.number_of_registrations}/${event.registration_limit}`
          : `${event.number_of_registrations}`;
        toolTipContent = `${toolTipContent}<br/>${this.$t(
          'EVENTS.LABEL.REGISTRATIONS'
        )}: ${regInfo}`;
      }
      if (event.number_of_waitlist_registrations) {
        toolTipContent = `${toolTipContent}<br/>${this.$t(
          'EVENTS.LABEL.WAITLIST_REGISTRATIONS'
        )}: ${event.number_of_waitlist_registrations}`;
      }
      if (event.substitute_instructors?.length) {
        toolTipContent = `${toolTipContent}<br/>${this.$t(
          'EVENTS.LABEL.SUBSTITUTES'
        )}: ${event.substitute_instructors.map(i => i.name).join(', ')}`;
      }
      tippy(info.el, {
        content: sanitizeHtmlFully(toolTipContent),
        placement: 'top',
        trigger: 'mouseenter focus',
        arrow: true,
        appendTo: document.body,
        allowHTML: true,
      });
    },
    async eventDrop(eventDropInfo) {
      const calendarEvent = eventDropInfo.event.extendedProps.event;
      calendarEvent.start_time = eventDropInfo.event.start;
      calendarEvent.date = eventDropInfo.event.start;
      const event = { ...eventDropInfo.event.extendedProps.event };
      await this.$store.dispatch('event/updateEvent', event);
      this.refetchEvents();
    },
    addEvent(date) {
      if (date) {
        this.$router.push({ name: 'events-new', query: { date } });
      } else {
        this.$router.push({ name: 'events-new' });
      }
    },
    handleDateSelect(selectInfo) {
      // if we want to add events via mouse click
      console.log(selectInfo);
    },
    handleEventClick(clickInfo) {
      const event = clickInfo.event.extendedProps.event;
      this.$store.dispatch('event/getEventDetails', event.id);
      const refreshCalendarFn = () => {
        this.refetchEvents();
      };
      this.$modal.show(EventStartStopModal, {
        refreshCalendarFn,
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    handleEvents(events) {},
  },
  mounted() {
    this.calendarRef = this.$refs.calendar;
    this.setInitialDate();
    this.setDisplayMonthAndYear();
  },
};
</script>

<style lang="scss">
@import '@/web/styles/components-variables.scss';

#calendar {
  .fc-scrollgrid {
    border-radius: 6px;
  }

  .fc-daygrid-day-number {
    color: $dark-gray;
  }

  .fc-event-time {
    color: $dark-gray;
    font-weight: 400;
    font-size: 14px;
    overflow: visible !important;
  }

  button.fc-button-primary {
    background-color: transparent;
    color: $dark-gray;
    border-color: $medium-gray;

    &.fc-button-active {
      background-color: $coral;
      color: $white;
    }
  }

  .fc-col-header-cell {
    font-weight: 500;
    padding: 0.5rem;
    text-align: left;

    .fc-col-header-cell-cushion {
      color: $dark-gray;
    }
  }

  .fc-event-main {
    padding-top: 0;
    padding-bottom: 0;
  }

  .fc-event-end,
  .fc-event-start {
    margin-left: 0;
    margin-right: 0;
  }

  .cancelled-event {
    .fc-event-title {
      text-decoration: line-through;
      opacity: 0.7;
    }
  }

  // add background colors to weekly and daily views
  .fc-timegrid-event {
    &.private_session {
      background-color: lighten($light-green, 30%) !important;
    }
    &.studio_event {
      background-color: lighten($blue, 33%) !important;
    }
    &.broadcasting_event {
      background-color: lighten($mustard, 56%) !important;
    }
    &.collaborative_group_event {
      background-color: lighten($brown, 53%) !important;
    }
  }

  .fc-day-today {
    background-color: transparent;
    .fc-daygrid-day-top {
      padding: 4px 4px;
      .fc-daygrid-day-number {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        background-color: #f26c67;
        color: #ffffff;
        display: inline-block;
        line-height: 20px;
        text-align: center;
      }
    }
  }

  .fc-col-header-cell {
    &.fc-day {
      &.fc-day-today {
        border-bottom-color: $coral;
        .fc-col-header-cell-cushion {
          color: $coral !important;
        }
      }
    }
  }

  .private_session {
    .fc-event-time {
      &:before {
        content: '';
        border-left: 3px solid $light-green;
        margin-right: 2px;
      }
    }

    .fc-event-title {
      &:before {
        color: $light-green;
        content: '\f505';
        font-family: 'Font Awesome 5 Pro';
        font-weight: 300;
        display: inline-block;
        margin-right: 5px;
      }
    }
  }

  .studio_event {
    .fc-event-time {
      &:before {
        content: '';
        border-left: 3px solid $blue;
        margin-right: 2px;
      }
    }

    .fc-event-title {
      &:before {
        color: $blue;
        content: '\f3c5';
        font-family: 'Font Awesome 5 Pro';
        font-weight: 300;
        display: inline-block;
        margin-right: 5px;
      }
    }
  }

  .meeting {
    .fc-event-time {
      &:before {
        content: '';
        border-left: 3px solid $forest;
        margin-right: 2px;
      }
    }

    .fc-event-title {
      &:before {
        color: $forest;
        content: '\f007';
        font-family: 'Font Awesome 5 Pro';
        font-weight: 300;
        display: inline-block;
        margin-right: 5px;
      }
    }
  }

  .broadcasting_event {
    .fc-event-time {
      &:before {
        content: '';
        border-left: 3px solid $mustard;
        margin-right: 2px;
      }
    }

    .fc-event-title {
      &:before {
        color: $mustard;
        content: '\f8a8';
        font-family: 'Font Awesome 5 Pro';
        font-weight: 300;
        display: inline-block;
        margin-right: 5px;
      }
    }
  }

  .collaborative_group_event {
    .fc-event-time {
      &:before {
        content: '';
        border-left: 3px solid $brown;
        margin-right: 2px;
      }
    }

    .fc-event-title {
      &:before {
        color: $brown;
        content: '\f0c0';
        font-family: 'Font Awesome 5 Pro';
        font-weight: 300;
        display: inline-block;
        margin-right: 5px;
      }
    }
  }

  .www-event {
    .fc-event-time {
      &:before {
        content: '';
        border-left: 3px solid $charcoal;
        margin-right: 2px;
      }
    }
  }

  .loading-calendar {
    position: absolute;
    z-index: 10;
    background-color: $lightest-gray;
    opacity: 0.4;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }
  .calendar-view {
    position: relative;
    @include media-breakpoint-down(sm) {
      display: none;
    }
    &.hide {
      display: none;
    }
  }
  .list-view {
    display: none;
    @include media-breakpoint-down(sm) {
      display: block;
    }
  }
  .show-list {
    display: block;
  }
}
</style>
